module.exports = function buildConnectCodexToYourBroker({ agentProofPoints }) {
  const SOURCE_VERIFIED_DATE = '2026-08-14';

  return {
    path: '/guides/connect-codex-to-your-broker',
    title: 'Connect Codex to Your Broker with BotSpot | MCP Guide',
    description: 'Configure BotSpot MCP in Codex, keep broker credentials inside BotSpot, inspect trading context, and stage broker orders behind explicit approval.',
    pageType: 'article',
    lastmod: SOURCE_VERIFIED_DATE,
    content: {
      eyebrow: 'Codex MCP broker guide',
      heading: 'Connect Codex to your broker through BotSpot',
      summary: 'Codex connects to BotSpot\'s remote MCP server, not directly to a brokerage account. BotSpot keeps the saved broker connection, exposes permitted research and trading tools, and requires separate approval before a staged order can execute.',
      primaryAction: {
        label: 'Create a BotSpot API key',
        url: '/account-settings/api-keys',
      },
      secondaryAction: {
        label: 'Open Codex MCP setup',
        url: '/agents#agents-client-codex',
      },
      proofPoints: agentProofPoints,
      sections: [
        {
          heading: '1. Understand the three-layer connection',
          paragraphs: [
            'Codex is the MCP client. BotSpot supplies the authenticated tools and workflow. A supported broker holds the account and executes eligible orders.',
            'Connect the brokerage account inside BotSpot first. Do not paste broker credentials into Codex, a prompt, a repository, or config.toml.',
          ],
          bullets: [
            'Codex to BotSpot: Streamable HTTP MCP.',
            'Codex authentication: BotSpot API key sent as a bearer token.',
            'BotSpot to broker: saved BotSpot broker connection.',
            'Execution: subject to BotSpot approval, broker support, account permissions, and order eligibility.',
          ],
        },
        {
          heading: '2. Create a BotSpot key and configure Codex',
          paragraphs: [
            'Create a BotSpot API key from Account Settings, store it in the BOTSPOT_API_KEY environment variable, then add the BotSpot server to Codex configuration.',
            'Use ~/.codex/config.toml for this host or .codex/config.toml inside a trusted project. Never commit the API key or write its value directly into the TOML file.',
          ],
          bullets: [
            '[mcp_servers.botspot]',
            'url = "https://mcp.botspot.trade/mcp"',
            'bearer_token_env_var = "BOTSPOT_API_KEY"',
          ],
        },
        {
          heading: '3. Verify with a read-only request',
          paragraphs: [
            'Restart the Codex client after changing MCP configuration. Run codex mcp list from the CLI or use /mcp inside Codex to confirm BotSpot is connected.',
            'Start with a narrow prompt such as: “List my BotSpot strategies and latest backtests. Do not create, edit, deploy, or trade.” Verify returned account context before allowing write-capable work.',
          ],
          bullets: [
            'A 401 response usually means the BotSpot API key was not loaded, expired, or was revoked.',
            'If BotSpot is missing, confirm the full endpoint ends with /mcp.',
            'If tool definitions changed, restart Codex so it reloads the MCP catalog.',
          ],
        },
        {
          heading: '4. Add a conservative Codex approval policy',
          paragraphs: [
            'OpenAI documents per-server tool approval controls. Add default_tools_approval_mode = "writes" under the BotSpot MCP server when you want Codex to prompt before tools not marked read-only.',
            'Codex approval does not replace BotSpot trade approval. It adds a client-side checkpoint before BotSpot creates the pending action.',
          ],
          bullets: [
            'default_tools_approval_mode = "writes"',
            'Use enabled_tools or disabled_tools only when you intentionally want a narrower tool surface.',
            'Do not configure blanket approval for write-capable trading tools.',
          ],
        },
        {
          heading: '5. Connect a supported broker inside BotSpot',
          paragraphs: [
            'Current BotSpot support includes stock, options, futures, and crypto connections. Support means BotSpot can connect to the listed provider; it does not prove that every account can place every order.',
          ],
          bullets: [
            'Stocks and options: Alpaca, Charles Schwab, and Tradier.',
            'Futures: Tradovate and TopstepX.',
            'Crypto: Alpaca, Kraken, Coinbase, WEEX, and Bitunix.',
            'Paper mode is available only for listed broker and authentication combinations.',
            'Broker permissions, market data, regions, fees, products, and order types remain account- and broker-specific.',
          ],
        },
        {
          heading: '6. Stage a trade without bypassing approval',
          paragraphs: [
            'Ask Codex for a concrete symbol, side, quantity, order type, time in force, and required prices. When the place_trade tool is available, BotSpot validates the request and creates a pending high-risk action.',
            'Review the short-lived BotSpot authorization page while signed in. Confirm broker, paper or live mode, symbol, quantity, order type, prices, option legs, and exits before approving.',
            'Do not treat creation of the approval as execution. Use BotSpot status or get_single_trade_status after approval before claiming the order was submitted, filled, rejected, or canceled.',
          ],
        },
        {
          heading: '7. Know when direct trading is unavailable',
          paragraphs: [
            'Direct trading depends on the place_trade scope being present for the current BotSpot API key and MCP session. A deliberately restricted token may expose research and account tools without exposing trade creation.',
            'Broker adapters can also reject unsupported order classes, account permissions, time-in-force values, or product combinations. BotSpot reports those failures without returning broker secrets.',
          ],
        },
        {
          heading: 'Safe starter prompts',
          bullets: [
            '“List my supported BotSpot broker connections and identify paper versus live mode. Do not change anything.”',
            '“Show my latest completed backtests and summarize their assumptions. Do not start a new backtest.”',
            '“Prepare a one-share market buy of SPY using an eligible paper connection. Stage it for BotSpot approval; do not claim it executed.”',
            '“Check the status of the pending trade action. Report only confirmed approval and broker status.”',
          ],
        },
        {
          heading: 'Limitations and risk',
          paragraphs: [
            'MCP configuration proves connectivity, not trading readiness. Generated analysis can be wrong, backtests remain simulations, and broker execution can differ because of prices, fills, latency, liquidity, fees, permissions, outages, and changing market conditions.',
            'Start with read-only inspection and paper mode where supported. Review every credential scope, order payload, approval screen, and status result.',
          ],
        },
      ],
      faq: [
        {
          question: 'Does Codex connect directly to my broker?',
          answer: 'No. Codex connects to BotSpot through MCP. BotSpot uses your saved supported broker connection for eligible account and execution workflows.',
        },
        {
          question: 'Can Codex see my broker API secret or password?',
          answer: 'BotSpot\'s MCP contract does not return raw broker secrets. Keep broker credentials inside BotSpot and keep the BotSpot API key outside prompts and committed files.',
        },
        {
          question: 'Can Codex place a trade without approval?',
          answer: 'No. BotSpot place_trade creates a pending approval request. The exact order must be reviewed and approved through BotSpot before broker submission.',
        },
        {
          question: 'Why is the trade tool missing?',
          answer: 'The current BotSpot token or session may not include the place_trade scope. Research and read-only tools can remain available under a narrower access policy.',
        },
        {
          question: 'Does a supported broker guarantee my order will work?',
          answer: 'No. Account permissions, asset eligibility, paper or live mode, market data, regional access, order types, and broker rules still apply.',
        },
      ],
      relatedLinks: [
        { label: 'MCP for Agents', url: '/agents' },
        { label: 'View supported brokers', url: '/brokers' },
        { label: 'Compare paper and live trading', url: '/guides/paper-trading-vs-live-trading' },
        { label: 'Build an AI trading bot', url: '/guides/how-to-build-an-ai-trading-bot' },
      ],
      sources: [
        {
          label: 'Official OpenAI Codex MCP documentation',
          url: 'https://learn.chatgpt.com/docs/extend/mcp?surface=cli',
        },
        { label: 'BotSpot MCP for Agents', url: '/agents' },
        { label: 'BotSpot broker connection manifest', url: '/.well-known/botspot-manifest.json' },
        { label: 'BotSpot supported brokers', url: '/brokers' },
        { label: 'BotSpot platform comparison', url: '/compare' },
        { label: 'BotSpot Terms of Service', url: '/terms' },
      ],
      verifiedAt: SOURCE_VERIFIED_DATE,
    },
  };
};
